import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Lock, Send, Sparkles, BookOpen, Target, Heart, TrendingUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { coachingAPI } from '../services/api';
import { LinkifyTextWithEmail } from '../utils/linkify';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CoachingPage = () => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [coachingTools, setCoachingTools] = useState([]);
  const { toast } = useToast();
  
  // Get user premium status from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isPremium = user.isPremium || false;

  useEffect(() => {
    // Load coaching tools from backend
    loadCoachingTools();
  }, []);

  const loadCoachingTools = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/admin/coaching-tools/public-list`);
      setCoachingTools(response.data.tools || []);
    } catch (error) {
      console.error('Could not load coaching tools', error);
    }
  };

  const icons = {
    'Career & Vocation': Target,
    'Physical Health': Heart,
    'Personal Growth': TrendingUp,
    'Relationships & Love': Heart,
    'Financial Well-Being': TrendingUp,
  };

  const handleToolClick = (tool) => {
    if (tool.isPremium && !isPremium) {
      toast({
        title: 'Premium Feature',
        description: 'Upgrade to Premium to access this coaching tool.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedTool(tool);
    setAiResponse('');
  };

  const handleAiCoaching = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: 'Empty prompt',
        description: 'Please enter a question or topic for AI coaching.',
        variant: 'destructive',
      });
      return;
    }

    if (!isPremium) {
      toast({
        title: 'Premium Feature',
        description: 'AI coaching is available for Premium members only.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await coachingAPI.ask({
        prompt: aiPrompt,
        domain: null
      });
      
      setAiResponse(response.response);
      
      toast({
        title: 'Success!',
        description: 'AI coach has provided guidance.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Could not get AI response. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Self-Coaching Tools
          </h1>
          <p className="text-lg text-gray-600">
            AI-powered tools to help you grow in every life domain
          </p>
        </div>

        {/* AI Coaching Section */}
        <Card className="p-8 bg-gradient-to-br from-green-600 to-green-700 text-white mb-12 shadow-xl">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">AI Life Coach</h2>
              <p className="text-green-100">
                Get personalized guidance powered by advanced AI. Ask anything about your life domains.
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-white/10 border-2 border-white/20 rounded-lg p-4 mb-6">
            <p className="text-sm text-white/90">
              <strong>⚠️ Important Disclaimer:</strong> The AI Life Coach provides general suggestions and insights for self-reflection and personal development. 
              These responses are not professional medical, psychological, legal, or financial advice. 
              For specific concerns or serious issues, please consult a licensed professional (therapist, counselor, doctor, financial advisor, etc.). 
              Always use your own judgment and seek professional help when needed.
            </p>
          </div>

          <div className="space-y-4">
            <Textarea
              placeholder="Ask your life coach... (e.g., 'How can I improve my work-life balance?')"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="bg-white text-gray-900 border-0 min-h-[100px]"
            />
            <Button
              onClick={handleAiCoaching}
              disabled={isLoading || !isPremium}
              className="bg-white text-green-600 hover:bg-gray-100 shadow-lg"
            >
              {isLoading ? (
                'Generating insights...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Get AI Coaching
                  {!isPremium && <Lock className="w-4 h-4 ml-2" />}
                </>
              )}
            </Button>

            {aiResponse && (
              <div className="bg-white rounded-lg p-6 text-gray-900 shadow-lg">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-lg">AI Coach Response</h3>
                </div>
                <p className="whitespace-pre-line text-gray-700">{aiResponse}</p>
              </div>
            )}

            {!isPremium && (
              <div className="bg-amber-500 text-white rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5" />
                  <span className="font-medium">AI Coaching requires Premium membership</span>
                </div>
                <Button
                  onClick={() => window.location.href = '/premium'}
                  variant="secondary"
                  size="sm"
                  className="bg-white text-amber-600 hover:bg-gray-100"
                >
                  Upgrade Now
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Coaching Tools Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Coaching Tools by Domain</h2>
          
          {coachingTools.length === 0 ? (
            <Card className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">More Tools Coming Soon</h3>
              <p className="text-gray-600">We're continuously adding new coaching resources to help you grow.</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coachingTools.map((tool) => {
                const Icon = icons[tool.domain] || BookOpen;
                return (
                  <Card
                    key={tool.id}
                    className="p-6 hover:shadow-xl transition-all cursor-pointer border-2 hover:border-green-300"
                    onClick={() => handleToolClick(tool)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Icon className="w-6 h-6 text-green-600" />
                      </div>
                      {tool.isPremium && (
                        <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-0">
                          <Lock className="w-3 h-3 mr-1" />
                          Premium
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{tool.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{tool.description}</p>
                    <Badge variant="outline" className="text-xs">
                      {tool.domain}
                    </Badge>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Premium CTA */}
        {!isPremium && (
          <Card className="p-8 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Unlock All Coaching Tools
                </h3>
                <p className="text-gray-700">
                  Get full access to AI coaching, progress tracking, and personalized insights
                </p>
              </div>
              <Button
                onClick={() => window.location.href = '/premium'}
                size="lg"
                className="bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg hover:shadow-xl"
              >
                Go Premium
              </Button>
            </div>
          </Card>
        )}

        {/* Tool Detail Modal */}
        {selectedTool && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-2xl w-full p-8 bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedTool.title}</h2>
                  <p className="text-sm text-gray-600 mt-1">{selectedTool.domain}</p>
                </div>
                <Button
                  onClick={() => setSelectedTool(null)}
                  variant="ghost"
                  size="sm"
                >
                  ✕
                </Button>
              </div>
              
              <p className="text-gray-600 mb-6">{selectedTool.description}</p>
              
              {selectedTool.content ? (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Instructions:</h3>
                  <div className="text-gray-700">
                    <LinkifyTextWithEmail text={selectedTool.content} />
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <p className="text-sm text-gray-700">
                    This is the {selectedTool.title} tool. Follow the instructions above to complete this exercise.
                  </p>
                </div>
              )}
              
              <Button
                onClick={() => setSelectedTool(null)}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Close
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachingPage;
